import { Link } from "wouter";
import { usePageTitle } from "@/hooks/use-page-title";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";

export default function PrivacyPage() {
  usePageTitle("Privacy Policy", "HealthWallet privacy policy detailing how we protect your personal and health information.");

  return (
    <div className="min-h-screen" data-testid="page-privacy">
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6">
        <Link href="/">
          <span className="inline-flex items-center gap-2 text-sm text-muted-foreground mb-6 cursor-pointer" data-testid="link-back-home">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </span>
        </Link>

        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2" data-testid="text-privacy-title">
          Privacy Policy
        </h1>
        <p className="text-muted-foreground mb-8">Last updated: February 15, 2026</p>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>1. Information We Collect</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>
                We collect personal information that you voluntarily provide when creating an account or using our platform, including your name, email address, phone number, and demographic information. This information is necessary to create and manage your HealthWallet account and to facilitate the services we provide.
              </p>
              <p>
                Financial information, including payment card details and bank account information, is collected and processed directly by Stripe, Inc., our third-party payment processor. HealthWallet does not store, process, or have access to your full payment card numbers, CVV codes, or bank account credentials. We receive only limited transaction data from Stripe, such as transaction amounts, dates, and confirmation statuses.
              </p>
              <p>
                We collect health-related information that you voluntarily share, such as condition interests when joining community funds or selecting condition-based wallets. This information reflects your stated interests and is not clinical data. We do not collect, store, or process clinical records, medical diagnoses, or treatment plans.
              </p>
              <p>
                We automatically collect usage data when you interact with the platform, including browser type, device information, IP address, pages visited, features used, and interaction timestamps. This data helps us improve the platform experience, diagnose technical issues, and maintain security.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>2. How We Use Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>
                We use the information we collect to provide, maintain, and improve the HealthWallet platform and its services. This includes creating and managing user accounts, processing wallet transactions, facilitating community fund participation, and enabling payment schedule management with healthcare providers.
              </p>
              <p>
                Your information is used to process transactions through our partnership with Stripe, including contributions to wallets and community funds, payment schedule installments, and subscription payments. We transmit only the minimum necessary information to Stripe to complete these transactions securely.
              </p>
              <p>
                We use your information to facilitate community support features, including matching contributors with community funds, enabling wallet sharing and discovery, and supporting emergency fundraising campaigns. Your public profile information and wallet details are only shared as you explicitly configure through your privacy settings.
              </p>
              <p>
                We communicate with you regarding your account activity, payment schedule reminders, contribution notifications, and important platform updates. We may also send promotional communications about new features or community initiatives, from which you can opt out at any time through your account settings.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>3. Information Sharing</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>
                We share necessary transaction data with Stripe, Inc. for payment processing purposes. This includes information required to process contributions, disbursements, and recurring payments. Stripe's handling of your data is governed by Stripe's own privacy policy and applicable financial regulations.
              </p>
              <p>
                We share limited information with hospital partners to facilitate payment schedules and provider payment arrangements. This includes patient name, wallet balance relevant to the arrangement, and payment schedule details. Hospital partners are contractually obligated to protect this information and use it only for the purpose of facilitating the agreed-upon payment arrangement.
              </p>
              <p>
                For employer partnership programs, we share only enrollment status and aggregate contribution information with participating employers. We do not share individual health conditions, wallet balances, community fund participation, or any health-related information with employers. Employers receive only confirmation of employee enrollment and summary data regarding their employer contribution programs.
              </p>
              <p>
                HealthWallet does not sell, rent, or trade your personal information to third parties for marketing or advertising purposes. We may disclose information when required by law, in response to valid legal process, or to protect the rights, property, or safety of HealthWallet, our users, or the public.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>4. Data Security</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>
                We implement bank-level encryption for all data transmitted between your device and our servers using TLS 1.2 or higher. All sensitive data stored in our systems is encrypted at rest using AES-256 encryption standards. Our security infrastructure is regularly reviewed and updated to address emerging threats.
              </p>
              <p>
                We maintain secure session management with automatic timeouts, multi-factor authentication options, and device recognition to prevent unauthorized access to your account. All authentication credentials are hashed using industry-standard algorithms and are never stored in plaintext.
              </p>
              <p>
                Our platform maintains comprehensive audit logging to track all significant account activities, including logins, transactions, and administrative actions. These logs are monitored for suspicious activity and retained in accordance with our data retention policies. Role-based access controls ensure that our team members can only access the minimum information necessary to perform their duties.
              </p>
              <p>
                While we implement robust security measures, no method of electronic transmission or storage is completely secure. We cannot guarantee absolute security of your data. In the event of a data breach that affects your personal information, we will notify you and relevant authorities in accordance with applicable law.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>5. Your Rights</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>
                You have the right to access the personal information we hold about you at any time. You can view and download most of your data directly through your account settings. For comprehensive data access requests, please contact us and we will provide a complete copy of your personal data within 30 days.
              </p>
              <p>
                You have the right to correct inaccurate or incomplete personal information. You can update most of your account information directly through the platform. For corrections to information that cannot be modified through the platform interface, please contact our support team.
              </p>
              <p>
                You have the right to request deletion of your personal data, subject to certain exceptions required by law or necessary for legitimate business purposes such as fraud prevention and legal compliance. Account deletion requests will be processed within 30 days, and we will confirm when your data has been removed from our active systems.
              </p>
              <p>
                You can opt out of non-essential communications at any time through your account notification settings or by using the unsubscribe link in our emails. Opting out of promotional communications will not affect transactional notifications related to your account activity, payment schedules, or security alerts.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>6. Minnesota Health Records Act</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>
                We comply with the Minnesota Health Records Act. Health-related information you share (such as condition interests for community funds) is protected under applicable Minnesota law. We do not store clinical records or diagnoses.
              </p>
              <p>
                The health-related information collected through the HealthWallet platform is limited to self-reported condition interests and community fund preferences. This information is used solely to connect users with relevant community support resources and is not shared with third parties for medical, underwriting, or discriminatory purposes.
              </p>
              <p>
                Under the Minnesota Health Records Act, you have specific rights regarding the handling of your health-related information, including the right to be informed about how this information is used, the right to access your records, and the right to request corrections. We are committed to upholding these rights and responding to all related requests promptly and in accordance with Minnesota law.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>7. Children's Privacy</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>
                The HealthWallet platform is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children. If you are under 18, you may only use the platform under the supervision and with the consent of a parent or legal guardian who has an active HealthWallet account.
              </p>
              <p>
                Family wallets may include beneficiaries who are minors, but all account management and financial transactions must be conducted by an adult account holder. If we discover that we have inadvertently collected personal information from a child under 18 without verified parental consent, we will take immediate steps to delete that information from our systems.
              </p>
              <p>
                Parents or guardians who believe their child has provided personal information to HealthWallet without their consent should contact us immediately. We will promptly investigate and remove any such information from our records.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>8. Data Retention</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>
                We retain your personal information for as long as your account is active and for a reasonable period thereafter to fulfill the purposes outlined in this policy, comply with our legal obligations, resolve disputes, and enforce our agreements. Active account data is maintained in our production systems with full security protections.
              </p>
              <p>
                Financial transaction records are retained for a minimum of seven years in accordance with applicable financial record-keeping requirements. Audit logs and security-related records are retained for a minimum of three years. Usage data and analytics information is retained in anonymized form for platform improvement purposes.
              </p>
              <p>
                Upon account deletion, your personal information is removed from our active systems within 30 days. Certain information may be retained in backup systems for up to 90 days before complete removal. Information required for legal compliance or ongoing dispute resolution may be retained for longer periods as required by applicable law.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>9. Changes to This Policy</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>
                We may update this Privacy Policy from time to time to reflect changes in our practices, technologies, legal requirements, or other factors. When we make material changes to this policy, we will notify you through the platform, via email to the address associated with your account, or through other appropriate communication channels.
              </p>
              <p>
                We will provide at least 30 days' notice before material changes take effect, giving you the opportunity to review the updated policy and make informed decisions about your continued use of the platform. The "Last updated" date at the top of this policy indicates when the most recent revisions were made.
              </p>
              <p>
                Your continued use of the HealthWallet platform after the effective date of any changes constitutes your acceptance of the updated Privacy Policy. If you do not agree with the revised policy, you should discontinue your use of the platform and contact us to request deletion of your account and personal data.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>10. Contact Us</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data handling practices, please contact us at:
              </p>
              <div>
                <p className="font-medium text-foreground">HealthWallet</p>
                <p>Minneapolis, MN</p>
                <p>Email: privacy@healthwallet.com</p>
              </div>
              <p>
                We take all privacy inquiries seriously and aim to respond within five business days. For data access, correction, or deletion requests, we will acknowledge receipt within 48 hours and complete the request within 30 days. If you are unsatisfied with our response, you may contact the Minnesota Attorney General's Office to file a complaint.
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="mt-8 p-4 rounded-md bg-muted text-xs text-muted-foreground leading-relaxed">
          This document is for informational purposes. HealthWallet provides technology services; Stripe processes and holds funds; hospitals facilitate payment arrangements. For questions about how Stripe handles your financial data, please refer to Stripe's privacy policy.
        </div>
      </div>
    </div>
  );
}
